import {
  ButtonGroup,
  IconButton,
  Stack,
  Text,
  useDisclosure,
  Button,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { t } from "i18next";
import { OsaWayItem } from "./OsaWayItem";
import { ProjectSharingModal } from "../../../../../shared/views/SharingModal/ProjectSharingModal";
import { useNavigate } from "react-router-dom";
import { appRoutes } from "../../../../../../App";
import { CreateOrUpdateProjectModal } from "../../../../../project/views/CreateOrUpdateProject/CreateOrUpdateProjectModal";
import { CreateOrUpdateOsaWayModal } from "../../../../../osaway/views/CreateOrUpdateRoute/CreateOrUpdateOsaWayModal";
import { useStores } from "../../../../../shared/stores/RootStore";
import { toast } from "../../../../../shared/views/CustomToaster";
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  UnlockIcon,
} from "../../../../../shared/views/icons";
import { CustomMenu } from "../../../../../shared/views/CustomMenu";
import { HlaStats } from "./HlaStats";

const i18prefix = "map:projectSummary";

const ProjectDetailBase: React.FC = () => {
  const { projectStore, authStore, filterStore, mapQueryStore } = useStores();
  const projectSharingModal = useDisclosure();
  const projectEditingModal = useDisclosure();
  const wayCreationModal = useDisclosure();

  const navigate = useNavigate();

  const project = projectStore.getSelected();

  const deleteProject = () => {
    if (window.confirm(t("shared:project.delete.popUp"))) {
      projectStore
        .deleteProject(project!.id)
        .then(() => {
          toast("success", t("shared:project.delete.success"));
        })
        .catch((e) => {
          toast("error", e.response?.statusText);
        })
        .finally(() => {
          navigate(appRoutes.home());
        });
    }
  };

  if (!project) return null;

  return (
    <Stack py={3} overflowY={"auto"} gap={4}>
      <Stack>
        <Text
          minH={7}
          flexGrow={1}
          px={2}
          fontWeight="semibold"
          textAlign="center"
        >
          {project.name}
        </Text>

        {authStore.isAuthenticated && (
          <ButtonGroup
            alignSelf="center"
            variant="outline"
            size="sm"
            attached
            colorPalette="primary"
          >
            <IconButton
              title={t(`${i18prefix}.edit`)}
              disabled={!project.permissions.canUpdate}
              onClick={() => {
                projectEditingModal.onOpen();
              }}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              title={t(`${i18prefix}.share`)}
              disabled={!project.permissions.canShare}
              onClick={() => {
                projectSharingModal.onOpen();
              }}
            >
              <UnlockIcon />
            </IconButton>

            <IconButton
              title={t(`${i18prefix}.delete`)}
              disabled={!project.permissions.canDelete}
              onClick={deleteProject}
            >
              <DeleteIcon />
            </IconButton>
          </ButtonGroup>
        )}

        {authStore.isAuthenticated && (
          <CustomMenu
            items={[
              {
                key: "cyclerap",
                text: "CycleRAP assessment",
                description:
                  "Routes are carefully analyzed according to the CycleRAP guidelines.",
                onClick: () => {
                  wayCreationModal.onOpen();
                },
              },
              {
                key: "high-level",
                text: "High-level assessment",
                description:
                  "A more scalable and less precise assessment of the infrastructure.",
                onClick: () => {
                  mapQueryStore.activate();
                  filterStore.resetFilters();
                },
              },
            ]}
          >
            <Button
              alignSelf={"center"}
              size="sm"
              fontSize="sm"
              colorPalette="primary"
              variant="ghost"
              disabled={!project.permissions.canCreate}
            >
              <AddIcon /> Start assessment
            </Button>
          </CustomMenu>
        )}

        {authStore.isSuperAdmin && (
          <Button
            size="sm"
            fontSize="sm"
            colorPalette="primary"
            variant="ghost"
            onClick={() => navigate(appRoutes.screen(project.id))}
          >
            <AddIcon /> {t("screen:createRoutes")}
          </Button>
        )}
      </Stack>

      <HlaStats />

      <Stack px={4}>
        <Text
          fontSize="xs"
          textTransform={"uppercase"}
          fontWeight={"semibold"}
          color={"gray.700"}
          pt={2}
        >
          CycleRAP assessments
        </Text>
        {!!filterStore.filteredOsaWays.length ? (
          <Stack gap={2}>
            {filterStore.filteredOsaWays.map((osaWay) => (
              <OsaWayItem key={osaWay.id} osaWay={osaWay} />
            ))}
          </Stack>
        ) : (
          <Text fontSize="sm">{t(`${i18prefix}.noRoutes`)}</Text>
        )}
      </Stack>

      {projectSharingModal.open && (
        <ProjectSharingModal onClose={projectSharingModal.onClose} />
      )}

      {projectEditingModal.open && (
        <CreateOrUpdateProjectModal
          project={project}
          onClose={projectEditingModal.onClose}
        />
      )}

      {wayCreationModal.open && (
        <CreateOrUpdateOsaWayModal onClose={wayCreationModal.onClose} />
      )}
    </Stack>
  );
};

export const ProjectDetail = observer(ProjectDetailBase);
